import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import Select from "react-select";
import axios from "axios";
import { AppContext } from "../contexts";
import occupationList from "../config/occupation";
import typeOfBusinessList from "../config/typeOfBusiness";
import Loader from "../components/loader";
import ErrorDialog from "../components/errorDialog";
import {
  normalizeName,
  normalizeRegNo,
  normalizeBizRegNo,
  normalizePostcode,
  normalizeEmail,
} from "../utils/normalize";
import { getErrorMsg } from "../utils/messageMapper";
import {
  MAX_L_BIZ_REG_NO,
  MAX_L_PASSPORT,
  MAX_L_PERSON_NAME,
  MAX_L_POSTCODE,
  MAX_L_VEHICLE_NO,
  MIN_L_BIZ_REG_NO,
  MIN_L_PASSPORT,
  MIN_L_PERSON_NAME,
  MIN_L_POSTCODE,
  MIN_L_VEHICLE_NO,
  REGEX_EMAIL,
  REGEX_PERSON_NAME,
} from "@digital/utils";

const SimpleQuote = ({ handleQuote, lang }) => {
  const state = useContext(AppContext);
  const { insured, vehicle, quotation } = state;
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const {
    register,
    handleSubmit,
    errors,
    setValue,
    setError,
    clearError,
    getValues,
  } = useForm({
    defaultValues: {
      registrationNo: vehicle.registrationNo,
      name: insured.name,
      nricPassport: insured.nricPassport,
      postcode: insured.postcode,
      email: insured.email,
      occpCode: insured.occpCode,
      typeOfBusiness: insured.typeOfBusiness,
      marital: insured.marital,
    },
  });

  const maritalStates = [
    { label: lang.maritalstatus.MARRIED, value: "M" },
    { label: lang.maritalstatus.SINGLE, value: "S" },
    { label: lang.maritalstatus.DIVORCED, value: "D" },
    { label: lang.maritalstatus.WIDOWED, value: "W" },
    { label: lang.maritalstatus.OTHERS, value: "O" },
  ];

  const isNewSSMId = (value) => /^\d{12}$/.test(value);

  const onSubmit = async (data) => {
    // console.log('disable all###########')
    // console.dir(data)

    setIsLoading(true);

    state.setIsNewSSMId(false);

    // To call for blacklist function first before getting quote
    if (insured.type === "P") {
      const response = await axios.post("/quote/blacklisted/", {
        name: data.name,
        id: data.nricPassport,
      });

      if (response.data.ISBLACKLISTED) {
        setIsError(true);
        setIsLoading(false);
        return;
      }

      data.bizRegNo = "";
    } else if (insured.type === "B") {
      if (isNewSSMId(data.bizRegNo)) {
        data.newSSMId = data.bizRegNo;
        data.bizRegNo = "";
        state.setIsNewSSMId(true);
      } else {
        data.bizRegNo = data.bizRegNo;
        data.newSSMId = "";
      }

      const businessIdToCheck = data.newSSMId || data.bizRegNo;
      if (businessIdToCheck) {
        const response = await axios.post("/quote/blacklisted/", {
          name: data.name,
          id: businessIdToCheck,
        });

        if (response.data.ISBLACKLISTED) {
          setIsError(true);
          setIsLoading(false);
          return;
        }
      }
    }

    const res = await axios.post("/quote/securemotor/get-quote", {
      ...data,
      coverageType: quotation.coverageType,
      type: insured.type,
    });
    const {
      RESPONSE_STATUS,
      MAKE_MODEL,
      SEAT,
      VEHICLE_NUMBER,
      MAKE_DESC,
      YEAR_MAKE,
      VEHICLE_CLASS,
      ENGINE_NUMBER,
      CHASSIS_NUMBER,
      ISM_NVIC_CODE,
      ISM_UPLOAD_NVIC_CODE,
      MODEL_DESC,
      CAPACITY,
    } = res.data?.ismData ?? {};

    // console.log('result: ', res.data)

    setIsLoading(false);
    if (!!RESPONSE_STATUS && RESPONSE_STATUS === "SUCCESS") {
      handleQuote(
        { ...data, vehicleYear: YEAR_MAKE, cc: CAPACITY },
        quotation.coverageType,
      );
      handleQuote(
        { ...data, vehicleYear: YEAR_MAKE, cc: CAPACITY },
        quotation.coverageType,
        true,
      );
      // if (quotation.coverageType === "sm") {
      //   handleQuote(data, "smve", false, true)
      // }

      /* Banca has requested to hide voluntary excess option */

      // if (quotation.coverageType === "csm") {
      //   handleQuote(
      //     { ...data, vehicleYear: YEAR_MAKE, cc: CAPACITY },
      //     "csmve",
      //     false,
      //     true,
      //   );
      // }
    } else {
      const msg = await getErrorMsg(res.data?.error);
      setErrorMessage(msg);
      setIsError(true);
    }
  };

  const handleMarital = (marital) => {
    setValue("marital", marital.value, true);
  };

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      padding: 10,
      color: state.isSelected ? "white" : "#5F5456",
      backgroundColor: state.isSelected ? "#CC0022" : "white",
    }),
    control: (provided, state) => {
      // none of react-select's styles are passed to <Control />
      const cstyle = {
        paddingTop: 12,
        border: "none",
        paddingLeft: 5,
      };
      return { ...provided, ...cstyle };
    },
    singleValue: (provided, state) => {
      const opacity = state.isDisabled ? 0.5 : 1;
      const transition = "opacity 300ms";

      return { ...provided, opacity, transition };
    },
  };

  const handleOccupation = (e) => {
    setValue("occpCode", e.value, true);
  };

  const handleTypeOfBusiness = (e) => {
    setValue("typeOfBusiness", e.value, true);
  };

  const defaultOccupation = occupationList.find(
    (occupation) => occupation.value === getValues("occpCode"),
  );
  const defaultTypeOfBusiness = typeOfBusinessList.find(
    (tob) => tob.value === getValues("typeOfBusiness"),
  );

  const isPrivate = () => (insured.type === "P" ? true : false);
  const isBusiness = () => (insured.type === "B" ? true : false);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col-md-4 mb-3">
            <div className="floating">
              <input
                type="text"
                name="registrationNo"
                placeholder="Vehicle Plate Number"
                className="floating__input"
                maxLength={MAX_L_VEHICLE_NO}
                onChange={(e) => normalizeRegNo(e)}
                ref={register({
                  required: lang?.simplequote?.field_require,
                  minLength: {
                    value: MIN_L_VEHICLE_NO,
                    message: lang?.simplequote?.valid_vehno,
                  },
                })}
              />
              <label
                htmlFor="input__username"
                className="floating__label"
                data-content={lang.simplequote.vehno}
              />
            </div>
            {errors?.registrationNo && (
              <div className="invalid-feedback d-block">
                {errors?.registrationNo?.message}
              </div>
            )}
          </div>
          <div className="col-md-4 mb-3" hidden={!isPrivate()}>
            <div className="floating">
              <input
                className="floating__input"
                type="text"
                name="nricPassport"
                placeholder="NRIC / Passport"
                maxLength={MAX_L_PASSPORT}
                ref={register({
                  required: isPrivate()
                    ? lang.simplequote.field_require
                    : false,
                  minLength: {
                    value: MIN_L_PASSPORT,
                    message: lang?.simplequote?.invalidFormat,
                  },
                  maxLength: {
                    value: MAX_L_PASSPORT,
                    message: lang?.simplequote?.invalidFormat,
                  },
                })}
              />
              <label
                htmlFor="input__username"
                className="floating__label"
                data-content={lang.simplequote.nric_passport}
              />
            </div>
            {errors?.nricPassport && (
              <div className="invalid-feedback d-block">
                {errors?.nricPassport?.message}
              </div>
            )}
          </div>

          <div className="col-md-4 mb-3" hidden={!isBusiness()}>
            <div className="floating">
              <input
                className="floating__input"
                type="text"
                name="bizRegNo"
                placeholder="Business Registration No."
                maxLength={MAX_L_BIZ_REG_NO}
                onChange={(e) => normalizeBizRegNo(e)}
                ref={register({
                  required: isBusiness()
                    ? lang.simplequote.field_require
                    : false,
                  minLength: {
                    value: MIN_L_BIZ_REG_NO,
                    message: lang?.simplequote?.invalidFormat,
                  },
                  maxLength: {
                    value: MAX_L_BIZ_REG_NO,
                    message: lang?.simplequote?.invalidFormat,
                  },
                })}
              />
              <label
                htmlFor="input__username"
                className="floating__label"
                data-content={lang.simplequote.busregno}
              />
            </div>
            {errors?.bizRegNo && (
              <div className="invalid-feedback d-block">
                {errors?.bizRegNo?.message}
              </div>
            )}
          </div>

          <div className="col-md-4 mb-3">
            <div className="floating">
              <input
                className="floating__input"
                type="text"
                name="name"
                placeholder={
                  isPrivate()
                    ? lang.simplequote.fullname
                    : isBusiness()
                      ? lang.simplequote.companyname
                      : ""
                }
                maxLength={MAX_L_PERSON_NAME}
                onChange={(e) => normalizeName(e)}
                ref={register({
                  required: lang?.simplequote?.field_require,
                  minLength: {
                    value: MIN_L_PERSON_NAME,
                    message: lang?.simplequote?.name_short,
                  },
                  pattern: {
                    value: REGEX_PERSON_NAME,
                    message: lang?.simplequote?.invalidFormat,
                  },
                })}
              />
              <label
                htmlFor="input__username"
                className="floating__label"
                data-content={
                  isPrivate()
                    ? lang.simplequote.fullname2
                    : isBusiness()
                      ? lang.simplequote.companyname
                      : ""
                }
              />
            </div>
            {errors?.name && (
              <div className="invalid-feedback d-block">
                {errors?.name?.message}
              </div>
            )}
          </div>

          <div className="col-md-4 mb-3">
            <div className="floating">
              <input
                className="floating__input"
                type="text"
                name="postcode"
                placeholder="Postcode"
                maxLength={MAX_L_POSTCODE}
                onChange={(e) => normalizePostcode(e)}
                ref={register({
                  required: lang?.simplequote?.field_require,
                  minLength: {
                    value: MIN_L_POSTCODE,
                    message: lang?.simplequote?.invalid_postcode,
                  },
                })}
                inputmode="numeric"
                pattern="[0-9]*"
              />
              <label
                htmlFor="input__username"
                className="floating__label"
                data-content={lang.simplequote.postcode}
              />
            </div>
            {errors?.postcode && (
              <div className="invalid-feedback d-block">
                {errors?.postcode?.message}
              </div>
            )}
          </div>

          <div className="col-md-4 mb-3">
            <div className="floating">
              <input
                className="floating__input"
                type="text"
                name="email"
                placeholder="Email"
                ref={register({
                  required: lang?.simplequote?.field_require,
                  pattern: {
                    value: REGEX_EMAIL,
                    message: lang.simplequote.valid_email,
                  },
                  onChange: (e) => normalizeEmail(e),
                })}
              />
              <label
                htmlFor="input__username"
                className="floating__label"
                data-content={lang.simplequote.email}
              />
            </div>
            {errors?.email && (
              <div className="invalid-feedback d-block">
                {errors?.email?.message}
              </div>
            )}
          </div>

          <div className="col-md-4 mb-3" hidden={!isPrivate()}>
            <div className="floating">
              <div className="select">
                <Select
                  styles={customStyles}
                  className="floating__input3"
                  options={maritalStates}
                  placeholder={lang.simplequote.dropdown_selection}
                  defaultValue={maritalStates.find(
                    (a) => a.value === insured.marital,
                  )}
                  onChange={(e) => handleMarital(e)}
                />
                <label className="floating-label">
                  {lang.simplequote.marital_status}
                </label>
              </div>
            </div>
            <input
              type="hidden"
              name="marital"
              ref={register({ required: isPrivate() ? true : false })}
            />
            {errors.marital && (
              <div className="invalid-feedback d-block">
                {lang.simplequote.field_require}
              </div>
            )}
          </div>

          <div className="col-md-4 mb-3" hidden={!isPrivate()}>
            <div className="floating">
              <div className="select">
                <Select
                  styles={customStyles}
                  className="floating__input3"
                  options={occupationList}
                  placeholder={lang.simplequote.dropdown_selection}
                  defaultValue={defaultOccupation}
                  onChange={(e) => handleOccupation(e)}
                />
                <label className="floating-label">
                  {lang.simplequote.occupation}
                </label>
              </div>

              <input
                className="floating__input"
                type="hidden"
                name="occpCode"
                ref={register({ required: isPrivate() ? true : false })}
              />
            </div>
            {errors.occpCode && (
              <div className="invalid-feedback d-block">
                {lang.simplequote.field_require}
              </div>
            )}
          </div>

          <div className="col-md-4 mb-3" hidden={!isBusiness()}>
            <div className="floating">
              <div className="select">
                <Select
                  styles={customStyles}
                  className="floating__input3"
                  options={typeOfBusinessList}
                  placeholder={lang.simplequote.dropdown_selection}
                  defaultValue={defaultTypeOfBusiness}
                  onChange={(e) => handleTypeOfBusiness(e)}
                />
                <label className="floating-label">
                  {lang.simplequote.type_business}
                </label>
              </div>

              <input
                className="floating__input"
                type="hidden"
                name="typeOfBusiness"
                ref={register({ required: isBusiness() ? true : false })}
              />
            </div>
            {errors.typeOfBusiness && (
              <div className="invalid-feedback d-block">
                {lang.simplequote.field_require}
              </div>
            )}
          </div>
        </div>
        <div className="row">
          <div className="col-12 mb-3">
            <div>
              {lang.simplequote.disclaimer_01}
              <a
                href="https://www.berjayasompo.com.my/privacy-notice"
                target="_blank"
              >
                {lang.simplequote.disclaimer_02}
              </a>
              {lang.simplequote.disclaimer_03}
            </div>
          </div>
        </div>
        <button type="submit" className="btn btn-primary btn-quick-quote">
          {lang.simplequote.submit_btn}
        </button>
      </form>

      {isLoading ? <Loader /> : null}
      <ErrorDialog
        isOpen={isError}
        toggle={() => setIsError((v) => !v)}
        message={errorMessage}
        type={insured.type}
        lang={lang}
      />
    </>
  );
};

export default SimpleQuote;
